import { useState, useEffect, createContext, useContext } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from './ui/dialog';
import { 
  Settings, Palette, Globe, Sun, Moon, Monitor, Leaf, Sunset, Waves
} from 'lucide-react';

// Theme definitions
const THEMES = {
  light: {
    id: 'light',
    name: 'Light',
    icon: Sun,
    description: 'Clean and bright',
    isDark: false
  },
  dark: {
    id: 'dark',
    name: 'Dark',
    icon: Moon,
    description: 'Easy on the eyes',
    isDark: true,
    className: 'dark'
  },
  darkBlue: {
    id: 'darkBlue',
    name: 'Dark Blue',
    icon: Waves,
    description: 'Deep ocean vibes',
    isDark: true,
    className: 'dark-blue'
  },
  system: {
    id: 'system',
    name: 'System',
    icon: Monitor,
    description: 'Match device settings',
    isDark: null // Uses system preference
  },
  nature: {
    id: 'nature',
    name: 'Nature',
    icon: Leaf,
    description: 'Calming greens',
    isDark: false,
    customVars: {
      '--primary': '120 35% 35%',
      '--accent': '35 80% 50%',
    }
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset',
    icon: Sunset,
    description: 'Warm and cozy',
    isDark: false,
    customVars: {
      '--primary': '15 70% 45%',
      '--accent': '35 90% 55%',
    }
  }
};

// Language definitions
const LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  es: { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  fr: { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  de: { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  pt: { code: 'pt', name: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
  zh: { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  ja: { code: 'ja', name: 'Japanese', flag: '🇯🇵', nativeName: '日本語' },
  ko: { code: 'ko', name: 'Korean', flag: '🇰🇷', nativeName: '한국어' },
};

// Preferences Context
const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const [theme, setTheme] = useState('light');
  const [language, setLanguage] = useState('en');

  // Load preferences from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('therentpayer_theme') || 'light';
    const savedLanguage = localStorage.getItem('therentpayer_language') || 'en';
    setTheme(savedTheme);
    setLanguage(savedLanguage);
    applyTheme(savedTheme);
  }, []);

  const applyTheme = (themeId) => {
    const root = document.documentElement;
    const selectedTheme = THEMES[themeId];
    
    // Reset custom vars and classes first
    root.style.removeProperty('--primary');
    root.style.removeProperty('--accent');
    root.classList.remove('dark', 'dark-blue');
    
    if (themeId === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
      }
    } else if (selectedTheme) {
      // Apply theme class (dark, dark-blue, etc.)
      if (selectedTheme.className) {
        root.classList.add(selectedTheme.className);
      }
      
      // Apply custom CSS vars if any
      if (selectedTheme.customVars) {
        Object.entries(selectedTheme.customVars).forEach(([key, value]) => {
          root.style.setProperty(key, value);
        });
      }
    }
  };

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('therentpayer_theme', newTheme);
    applyTheme(newTheme);
  };

  const updateLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('therentpayer_language', newLanguage);
    document.documentElement.lang = newLanguage;
  };

  return (
    <PreferencesContext.Provider value={{ 
      theme, 
      language, 
      updateTheme, 
      updateLanguage,
      themes: THEMES,
      languages: LANGUAGES 
    }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
}

// Preferences Dialog Component
export function PreferencesDialog({ trigger }) {
  const { theme, language, updateTheme, updateLanguage, themes, languages } = usePreferences();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" data-testid="preferences-button">
            <Settings className="h-5 w-5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
            <Settings className="h-5 w-5 text-primary" />
            Preferences
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Theme Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <Palette className="h-4 w-4" />
              Theme
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {Object.values(themes).map((t) => {
                const Icon = t.icon;
                const isSelected = theme === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => updateTheme(t.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                    data-testid={`theme-${t.id}`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Language Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <Globe className="h-4 w-4" />
              Language
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(languages).map((lang) => {
                const isSelected = language === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => updateLanguage(lang.code)}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                    data-testid={`language-${lang.code}`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <div className="text-left">
                      <p className="font-medium text-sm">{lang.name}</p>
                      <p className="text-xs text-muted-foreground">{lang.nativeName}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => setOpen(false)} data-testid="close-preferences">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Quick Theme Toggle Button (for header)
export function ThemeToggle() {
  const { theme, updateTheme, themes } = usePreferences();
  
  const cycleTheme = () => {
    const themeKeys = Object.keys(themes);
    const currentIndex = themeKeys.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    updateTheme(themeKeys[nextIndex]);
  };

  const CurrentIcon = themes[theme]?.icon || Sun;

  return (
    <Button 
      variant="ghost" 
      size="icon" 
      onClick={cycleTheme}
      className="h-9 w-9"
      data-testid="theme-toggle"
    >
      <CurrentIcon className="h-5 w-5" />
    </Button>
  );
}

export default PreferencesDialog;
